import { redoStyle, undoStyle } from './styleHandling';
import {
  redoColors,
  redoLogo,
  redoPattern,
  redoText,
  toolbarActive,
  undoColors,
  undoLogo,
  undoPatterns,
  undoText,
} from './webGL';

export function updateUndoState(undoList) {
  if (!Array.isArray(undoList) || typeof window.setUndoState !== 'function') return;

  // Always disable undo if the list is empty
  if (undoList.length === 0) {
    window.setUndoState(false);
    return;
  }

  // Check specific conditions for each toolbar type
  // Note: For logo mode, we should enable undo if undoList has items,
  // regardless of window.decals.length, because user can undo to restore logos
  if (toolbarActive.value === 'style' && undoList.length === 1) {
    window.setUndoState(false);
    return;
  }
  if (toolbarActive.value === 'text' && undoList.length === 0) {
    window.setUndoState(false);
    return;
  }
  if (toolbarActive.value === 'pattern' && undoList.length === 0) {
    window.setUndoState(false);
    return;
  }
  if (toolbarActive.value === 'color' && undoList.length === 0) {
    window.setUndoState(false);
    return;
  }

  // Enable undo if we have items in the list and we're in the correct mode
  window.setUndoState(true);
}

export function updateRedoState(redoList) {
  if (!Array.isArray(redoList) || typeof window.setRedoState !== 'function') return;
  if (redoList.length === 0) {
    window.setRedoState(false);
  } else {
    window.setRedoState(true);
  }
}

export function updateClearState() {
  if (typeof window.setClearState !== 'function') return;

  switch (toolbarActive.value) {
    case 'logo':
      if (undoLogo?.length > 0 || redoLogo?.length > 0) {
        window.setClearState(true);
      } else {
        window.setClearState(false);
      }
      break;

    case 'text':
      if (undoText?.length > 0 || redoText?.length > 0) {
        window.setClearState(true);
      } else {
        window.setClearState(false);
      }
      break;

    case 'style':
      if (undoStyle?.length > 0 || redoStyle?.length > 0) {
        window.setClearState(true);
      } else {
        window.setClearState(false);
      }
      break;

    case 'color':
      if (undoColors?.length > 0 || redoColors?.length > 0) {
        window.setClearState(true);
      } else {
        window.setClearState(false);
      }
      break;

    case 'pattern':
      if (undoPatterns.length > 0 || redoPattern?.length > 0) {
        window.setClearState(true);
      } else {
        window.setClearState(false);
      }
      break;

    default:
      window.setClearState(false);
      break;
  }
}

export function handleToolbarChanged(newValue) {
  switch (newValue) {
    case 'style':
      updateRedoState(redoStyle);
      updateUndoState(undoStyle);
      break;
    case 'logo':
      updateRedoState(redoLogo);
      updateUndoState(undoLogo);
      break;
    case 'color':
      updateRedoState(redoColors);
      updateUndoState(undoColors);
      break;
    case 'pattern':
      updateRedoState(redoPattern);
      updateUndoState(undoPatterns);
      break;
    case 'text':
      updateRedoState(redoText);
      updateUndoState(undoText);
      break;
    default:
      console.log('ERROR');
  }
}

export function createArrayWithLengthCallback(initialArray = [], callback) {
  const arrayMethods = ['push', 'pop', 'shift', 'unshift', 'splice', 'sort', 'reverse'];

  return new Proxy(initialArray, {
    get(target, property) {
      // Intercept array methods to trigger callback
      if (arrayMethods.includes(property)) {
        return function (...args) {
          const oldLength = target.length;
          const result = Array.prototype[property].apply(target, args);
          const newLength = target.length;

          if (oldLength !== newLength) {
            callback(newLength, oldLength, target);
          }

          return result;
        };
      }

      // Return normal property access
      return target[property];
    },
    set(target, property, value) {
      const oldLength = target.length;
      target[property] = value;
      const newLength = target.length;

      if (oldLength !== newLength) {
        callback(newLength, oldLength, target);
      }

      return true;
    },
  });
}

export class ObservableValue {
  constructor(value) {
    this.listeners = [];
    this._value = value;
  }

  addListener(listener, scope) {
    const boundListener = scope ? listener.bind(scope) : listener;
    this.listeners.push(boundListener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== boundListener);
    };
  }

  removeListener(listener) {
    this.listeners = this.listeners.filter((currentListener) => currentListener !== listener);
  }

  removeListenerBaseOnName(listener) {
    this.listeners = this.listeners.filter(
      (currentListener) => !currentListener.name.includes(listener.name),
    );
  }

  notify(data) {
    this.listeners.forEach((listener) => {
      if (typeof listener === 'function') {
        listener(data);
      }
    });
  }

  get value() {
    return this._value;
  }

  set value(newValue) {
    if (this._value === newValue) return;
    this._value = newValue;
    this.notify(newValue);
  }
}
